import { ControlType } from 'utility/constant'

const stripHtml = text => {
    if (!text) return ''
    const tmp = document.createElement('div')
    tmp.innerHTML = text
    return tmp.textContent || tmp.innerText || ''
}

export const ASSIGNMENT_STRATEGY_FIXED = 2

export const MAX_TRIGGER_ANSWERS = 25
export const MAX_ASSIGNEES = 25

/** Legacy ids when lookup is unavailable (live API uses InternalUser/Team/Vendor*). */
export const ASSIGNEE_TYPE_KEYS = {
    user: 1,
    team: 2,
    vendorCompany: 3,
    vendorContact: 4,
}

export const ASSIGNEE_OPTION_UI = {
    user: {
        title: '👤 A specific person on my team',
        desc: 'An internal eAuditor user — gets an email + in-app notification.',
    },
    team: {
        title: '👥 A team or group',
        desc: 'A whole team — members can pick up the ticket.',
    },
    'vendor-company': {
        title: '🏢 An outside vendor company',
        desc: "Routes to the company's on-file primary contact.",
    },
    'vendor-contact': {
        title: '📇 A specific vendor contact',
        desc: 'A named person at an outside vendor company.',
    },
    role: {
        title: '🎭 A role',
        desc: 'Routes to users with this role in your organization.',
    },
}

/** Resolve multi-choice options from template root TemplateResponseType (same as inspection preview). */
export const resolveTemplateMultipleResponseOptions = (question, template) => {
    if (!question || !template?.TemplateResponseType) {
        return []
    }
    const responseType = template.TemplateResponseType
    const standardOpts = responseType.MultipleResponse || []
    const userOpts = responseType.UserMultipleResponse || []
    const questionResponseId = question?.QuestionResponseId
    const questionId = question?.Id

    const findSeed = opts =>
        (opts || []).find(
            opt =>
                opt.Id === questionResponseId ||
                opt.GroupId === questionResponseId ||
                (questionResponseId === 0 &&
                    (opt.GroupId === questionId || opt.Id === questionId)),
        )

    const seed = findSeed(userOpts) || findSeed(standardOpts)
    if (seed) {
        const groupId = seed.GroupId ?? seed.Id
        const userGroup = userOpts.filter(opt => opt.GroupId === groupId)
        if (userGroup.length) {
            return userGroup
        }
        return standardOpts.filter(opt => opt.GroupId === groupId)
    }

    const optionMatcher = opt =>
        opt.GroupId === questionResponseId ||
        opt.Id === questionResponseId ||
        (questionResponseId === 0 &&
            (opt.GroupId === questionId || opt.Id === questionId))
    const userMatched = userOpts.filter(optionMatcher)
    if (userMatched.length) {
        return userMatched
    }
    return standardOpts.filter(optionMatcher)
}

const normalizeQuestionAnswers = (q, template) => {
    let multiAnswers =
        q.MultiAnswers ||
        q.MultipleAnswers ||
        q.CustomerMultiResponses ||
        q.Answers?.MultipleResponse ||
        []
    if (!Array.isArray(multiAnswers)) {
        multiAnswers = multiAnswers ? [multiAnswers] : []
    }

    let singleAnswers = q.SingleAnswers || q.Answers?.SingleResponse
    if (singleAnswers && !Array.isArray(singleAnswers)) {
        singleAnswers = [singleAnswers]
    }
    if (!Array.isArray(singleAnswers)) {
        singleAnswers = []
    }

    if (!multiAnswers.length && !singleAnswers.length && template) {
        multiAnswers = resolveTemplateMultipleResponseOptions(q, template)
    }

    return { multiAnswers, singleAnswers }
}

export const extractTemplateQuestions = template => {
    const sections = template?.Sections || []
    const questions = []
    sections.forEach(section => {
        const sectionQuestions = section.Questions || []
        sectionQuestions.forEach(q => {
            const rtId = Number(q.QuestionResponseId)
            if (
                rtId === ControlType.Label ||
                rtId === ControlType.Header ||
                rtId === ControlType.AutoNumber
            ) {
                return
            }
            const { multiAnswers, singleAnswers } = normalizeQuestionAnswers(
                q,
                template,
            )
            questions.push({
                auditQuestionId: q.Id ?? q.QuestionId,
                questionText: stripHtml(
                    q.QuestionText || q.Question || q.DisplayText || 'Untitled',
                ),
                sectionName: section.SectionName || section.Name || '',
                questionResponseId: rtId,
                masterListId: q.MasterListId ?? q.masterListId ?? null,
                multiAnswers: Array.isArray(multiAnswers) ? multiAnswers : [],
                singleAnswers: Array.isArray(singleAnswers)
                    ? singleAnswers
                    : [],
            })
        })
    })
    return questions.filter(
        q => q.auditQuestionId != null && q.auditQuestionId !== '',
    )
}

export const buildQuestionDropdownOptions = questions => {
    const seen = new Set()
    return (questions || [])
        .filter(q => {
            const id = q.auditQuestionId
            if (id == null || id === '') return false
            const key = String(id)
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
        .map(q => ({
            key: String(q.auditQuestionId),
            value: Number(q.auditQuestionId) || q.auditQuestionId,
            text: q.sectionName
                ? `${q.questionText} — ${q.sectionName}`
                : q.questionText || 'Untitled',
            description: q.sectionName || '',
        }))
}

export const filterQuestionDropdownOptions = (options, query) => {
    const q = String(query || '')
        .trim()
        .toLowerCase()
    if (!q) return options
    return options.filter(
        opt =>
            String(opt.text || '')
                .toLowerCase()
                .includes(q) ||
            String(opt.description || '')
                .toLowerCase()
                .includes(q),
    )
}

export const getQuestionAnswerOptions = question => {
    if (!question) return []
    if (question.multiAnswers?.length) {
        return question.multiAnswers.map(a => ({
            id: a.Id ?? a.ResponseId ?? a.id,
            text:
                stripHtml(
                    a.DisplayText ??
                        a.displayText ??
                        a.Response ??
                        a.Text ??
                        '',
                ) || String(a.Id ?? a.ResponseId ?? ''),
            score: a.Score ?? a.score,
        })).filter(a => a.text)
    }
    if (question.singleAnswers?.length) {
        return question.singleAnswers.map(a => ({
            id: a.Id ?? a.ResponseId ?? a.id,
            text:
                stripHtml(
                    a.DisplayText ?? a.Response ?? a.Text ?? '',
                ) || String(a.Id ?? a.ResponseId ?? ''),
        })).filter(a => a.text)
    }
    return []
}

/** Mockup-style labels for Step 2 answer cards (from real answer text). */
export const getAnswerOptionPresentation = text => {
    const t = String(text || '').toLowerCase()
    if (
        t.includes('non') ||
        t.includes('fail') ||
        t === 'no' ||
        t.startsWith('no ')
    ) {
        return {
            icon: '❌',
            title: text,
            desc: 'Most common — fires when the auditor flags this as a fail.',
        }
    }
    if (t.includes('partial') || t.includes('attention')) {
        return {
            icon: '⚠',
            title: text,
            desc: 'Fires when there is a partial pass / needs attention.',
        }
    }
    if (t.includes('yes') || t.includes('pass') || t.includes('conform')) {
        return {
            icon: '✓',
            title: text,
            desc: 'Fires when the auditor selects this answer.',
        }
    }
    return {
        icon: '●',
        title: text,
        desc: 'Fires when the auditor selects this answer.',
    }
}

export const normalizeTriggerOperatorList = response =>
    (Array.isArray(response) ? response : []).map(item => ({
        id: item.TriggerOperatorId ?? item.Id ?? item.id,
        name:
            item.OperatorName ??
            item.Name ??
            item.name ??
            '',
    }))

export const enrichQuestionsWithMasterAnswers = async (questions, fetchItems) => {
    const list = questions || []
    const masterIds = [
        ...new Set(
            list
                .filter(
                    q =>
                        !q.multiAnswers?.length &&
                        !q.singleAnswers?.length &&
                        q.masterListId,
                )
                .map(q => q.masterListId),
        ),
    ]
    if (!masterIds.length) return list

    const itemsByList = {}
    await Promise.all(
        masterIds.map(async id => {
            try {
                const res = await fetchItems(id)
                itemsByList[id] = Array.isArray(res)
                    ? res
                    : res?.Items || res?.Data || []
            } catch {
                itemsByList[id] = []
            }
        }),
    )

    return list.map(q => {
        if (q.multiAnswers?.length || q.singleAnswers?.length) return q
        const raw = itemsByList[q.masterListId]
        if (!raw?.length) return q
        const multiAnswers = raw.map(item => ({
            Id: item.ItemId ?? item.Id ?? item.id,
            DisplayText:
                item.DisplayText ??
                item.Text ??
                item.Label ??
                item.Name ??
                '',
            Score: item.Score ?? item.score,
        }))
        return { ...q, multiAnswers }
    })
}

export const normalizeRuleList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Rules || response?.Data || []
    return list.map(normalizeRule)
}

const normalizeTriggerKey = value =>
    String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

const appendTriggerValue = (values, raw) => {
    const text = String(raw || '').trim()
    if (!text) return
    if (!values.some(v => normalizeTriggerKey(v) === normalizeTriggerKey(text))) {
        values.push(text)
    }
}

/** Prefer TriggerValues[]; fall back to child rows / singular TriggerValue. */
export const normalizeTriggerValues = rule => {
    const values = []
    const multi =
        rule?.TriggerValues ??
        rule?.triggerValues ??
        rule?.RuleTriggerAnswers ??
        rule?.ruleTriggerAnswers ??
        rule?.TriggerAnswers ??
        rule?.triggerAnswers

    if (Array.isArray(multi)) {
        multi.forEach(item => {
            if (typeof item === 'string' || typeof item === 'number') {
                appendTriggerValue(values, item)
                return
            }
            if (item && typeof item === 'object') {
                appendTriggerValue(
                    values,
                    item.TriggerValue ??
                        item.triggerValue ??
                        item.AnswerText ??
                        item.answerText ??
                        item.Value ??
                        item.value ??
                        item.DisplayText ??
                        item.displayText,
                )
            }
        })
    } else if (typeof multi === 'string') {
        multi.split(/[;,|]/).forEach(part => appendTriggerValue(values, part))
    }

    if (!values.length) {
        appendTriggerValue(values, rule?.TriggerValue ?? rule?.triggerValue)
    }
    return values
}

export const isTriggerValueSelected = (selectedValues, optionText) => {
    const key = normalizeTriggerKey(optionText)
    return (selectedValues || []).some(v => normalizeTriggerKey(v) === key)
}

export const alignTriggerValuesToQuestion = (question, apiValues) => {
    const options = getQuestionAnswerOptions(question)
    return (apiValues || []).map(apiVal => {
        const key = normalizeTriggerKey(apiVal)
        const match = options.find(o => normalizeTriggerKey(o.text) === key)
        return match ? match.text : String(apiVal).trim()
    })
}

/** Prefer Assignees[]; fall back to singular FixedAssignedTo* for older APIs. */
export const normalizeAssignees = rule => {
    const multi = rule?.Assignees ?? rule?.assignees
    if (Array.isArray(multi) && multi.length) {
        return multi
            .map(a => ({
                typeId: Number(
                    a.FixedAssignedToTypeId ??
                        a.AssignedToTypeId ??
                        a.typeId,
                ),
                assignedToId:
                    a.FixedAssignedToId ??
                    a.AssignedToId ??
                    a.assignedToId ??
                    null,
                vendorCompanyId:
                    a.VendorCompanyId ?? a.vendorCompanyId ?? null,
                assigneeName:
                    a.AssigneeName ??
                    a.DisplayName ??
                    a.assigneeName ??
                    '',
            }))
            .filter(a => Number.isFinite(a.typeId))
    }
    const typeId =
        rule?.FixedAssignedToTypeId ??
        rule?.AssignedToTypeId ??
        rule?.fixedAssignedToTypeId
    if (typeId == null || typeId === '') return []
    return [
        {
            typeId: Number(typeId),
            assignedToId:
                rule.FixedAssignedToId ??
                rule.AssignedToId ??
                rule.fixedAssignedToId ??
                null,
            vendorCompanyId:
                rule.VendorCompanyId ?? rule.vendorCompanyId ?? null,
        },
    ]
}

const isTruthyFlag = value =>
    value === true || value === 1 || value === '1' || value === 'true'

const isFalsyFlag = value =>
    value === false || value === 0 || value === '0' || value === 'false'

const parseIdList = raw => {
    let parts = []
    if (Array.isArray(raw)) {
        parts = raw
    } else if (typeof raw === 'string') {
        parts = raw.split(/[;,]/)
    } else if (raw != null && raw !== '') {
        parts = [raw]
    }
    const ids = []
    parts.forEach(part => {
        const raw =
            part && typeof part === 'object'
                ? part.CustomerId ??
                  part.customerId ??
                  part.unique_id ??
                  part.UniqueId ??
                  part.Id ??
                  part.id
                : part
        const n = Number(typeof raw === 'string' ? raw.trim() : raw)
        if (Number.isFinite(n) && n > 0 && !ids.includes(n)) {
            ids.push(n)
        }
    })
    return ids
}

/** External suppliers from CQATS are not company customers. */
export const isCqatsSource = row =>
    String(row?.Source ?? row?.source ?? '')
        .trim()
        .toUpperCase() === 'CQATS'

/** GET Eauditor/Customer?CallFrom=1 — value is unique_id, label is customer_name. */
export const normalizeCompanyCustomers = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    const seen = new Set()
    return list
        .filter(item => item && !isCqatsSource(item))
        .map(item => ({
            id: Number(
                item.unique_id ??
                    item.UniqueId ??
                    item.CustomerId ??
                    item.Id ??
                    item.id,
            ),
            name: String(
                item.customer_name ??
                    item.CustomerName ??
                    item.DisplayText ??
                    item.Name ??
                    item.name ??
                    '',
            ).trim(),
        }))
        .filter(item => {
            if (!Number.isFinite(item.id) || item.id <= 0 || !item.name) {
                return false
            }
            if (seen.has(item.id)) return false
            seen.add(item.id)
            return true
        })
        .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        )
}

const customerScopeSource = rule => {
    const nested = [rule?.Rule, rule?.Data, rule?.ActionPlanRule].find(
        item => item && typeof item === 'object' && !Array.isArray(item),
    )
    return nested ? { ...nested, ...rule } : rule
}

export const normalizeRuleCustomerScope = rule => {
    const source = customerScopeSource(rule)
    const flag =
        source?.AppliesToAllCustomers ?? source?.appliesToAllCustomers
    const fromIds = parseIdList(
        source?.CustomerIds ?? source?.customerIds ?? source?.CustomerIDs,
    )
    const named = (Array.isArray(source?.Customers ?? source?.customers)
        ? source.Customers ?? source.customers
        : []
    )
        .map(c => ({
            id: Number(
                c?.CustomerId ?? c?.customerId ?? c?.unique_id ?? c?.Id ?? c?.id,
            ),
            name: String(
                c?.CustomerName ??
                    c?.customerName ??
                    c?.customer_name ??
                    c?.Name ??
                    c?.name ??
                    '',
            ).trim(),
        }))
        .filter(c => Number.isFinite(c.id) && c.id > 0)

    const customerIds = fromIds.length ? fromIds : named.map(c => c.id)
    // Saved ids win. A default AppliesToAllCustomers=true must not hide them.
    let appliesToAllCustomers = true
    if (customerIds.length > 0) {
        appliesToAllCustomers = false
    } else if (isFalsyFlag(flag)) {
        appliesToAllCustomers = false
    } else if (isTruthyFlag(flag) || flag == null) {
        appliesToAllCustomers = true
    }

    return {
        appliesToAllCustomers,
        customerIds: appliesToAllCustomers ? [] : customerIds,
        customers: appliesToAllCustomers ? [] : named,
    }
}

export const CUSTOMER_SUMMARY_LIMIT = 3

/** First 3 names, then "+N more". Empty list is All customers. */
export const formatCustomerSummary = (
    names,
    { max = CUSTOMER_SUMMARY_LIMIT, allLabel = 'All customers' } = {},
) => {
    const list = (names || []).map(n => String(n).trim()).filter(Boolean)
    if (!list.length) return allLabel
    if (list.length <= max) return list.join(', ')
    const extra = list.length - max
    return `${list.slice(0, max).join(', ')} +${extra} more`
}

export const getRuleCustomerNames = rule => {
    if (!rule || rule.appliesToAllCustomers !== false) return []
    if (rule.customers?.length) {
        const named = rule.customers.map(c => c.name).filter(Boolean)
        if (named.length) return named
    }
    return (rule.customerIds || []).map(id => `Customer #${id}`)
}

export const formatRuleCustomersLabel = rule => {
    if (!rule || rule.appliesToAllCustomers !== false) return 'All customers'
    return formatCustomerSummary(getRuleCustomerNames(rule), {
        allLabel: 'Selected customers',
    })
}

export const normalizeRule = rule => {
    const triggerValues = normalizeTriggerValues(rule)
    const assignees = normalizeAssignees(rule)
    const primary = assignees[0] || {}
    const customerScope = normalizeRuleCustomerScope(rule)
    return {
        id: rule.RuleId ?? rule.Id ?? rule.id,
        auditQuestionId:
            rule.AuditQuestionId ?? rule.QuestionId ?? rule.auditQuestionId,
        questionText: stripHtml(
            rule.QuestionText ?? rule.QuestionName ?? rule.questionText ?? '',
        ),
        sectionName: rule.SectionName ?? rule.sectionName ?? '',
        triggerValue: triggerValues[0] || '',
        triggerValues,
        triggerOperatorId: rule.TriggerOperatorId ?? rule.triggerOperatorId ?? 1,
        ticketType: rule.TicketType ?? rule.ticketType ?? '',
        fixedAssignedToTypeId:
            primary.typeId ??
            rule.FixedAssignedToTypeId ??
            rule.AssignedToTypeId ??
            rule.fixedAssignedToTypeId,
        fixedAssignedToTypeName:
            rule.FixedAssignedToTypeName ??
            rule.AssignedToTypeName ??
            rule.fixedAssignedToTypeName ??
            '',
        fixedAssignedToId:
            primary.assignedToId ??
            rule.FixedAssignedToId ??
            rule.AssignedToId ??
            rule.fixedAssignedToId,
        assignees,
        assigneeName:
            rule.AssigneeName ??
            rule.AssignedToName ??
            rule.FixedAssignedToName ??
            rule.assigneeName ??
            '',
        priorityId: rule.PriorityId ?? rule.priorityId,
        priorityName: rule.PriorityName ?? rule.priorityName ?? '',
        ackSlaMinutes:
            rule.DefaultAckSlaMinutes ??
            rule.AcknowledgeHours ??
            rule.ackSlaMinutes,
        resolveSlaMinutes:
            rule.DefaultResolveSlaMinutes ??
            rule.ResolveHours ??
            rule.resolveSlaMinutes,
        categoryId: rule.CategoryId ?? rule.categoryId,
        categoryName: rule.CategoryName ?? rule.categoryName ?? '',
        assignmentStrategy:
            rule.AssignmentStrategy ??
            rule.assignmentStrategy ??
            ASSIGNMENT_STRATEGY_FIXED,
        isEnabled: rule.IsEnabled !== false && rule.isEnabled !== false,
        ticketsLast30Days:
            rule.TicketsLast30Days ??
            rule.TicketsCreatedLast30Days ??
            rule.ticketsLast30Days ??
            0,
        escalationPolicyId:
            rule.EscalationPolicyId ?? rule.escalationPolicyId ?? null,
        escalationPolicyName:
            rule.EscalationPolicyName ?? rule.escalationPolicyName ?? '',
        templateId: rule.TemplateId ?? rule.templateId,
        appliesToAllCustomers: customerScope.appliesToAllCustomers,
        customerIds: customerScope.customerIds,
        customers: customerScope.customers,
    }
}

export const enrichRulesWithQuestions = (rules, questions) =>
    rules.map(rule => {
        const q = questions.find(
            x => Number(x.auditQuestionId) === Number(rule.auditQuestionId),
        )
        return {
            ...rule,
            questionText: rule.questionText || q?.questionText || '',
            sectionName: rule.sectionName || q?.sectionName || '',
        }
    })

export const resolveRuleAssigneeName = (rule, lookups = {}) => {
    const {
        teamMembers = [],
        teams = [],
        vendorCompanies = [],
        assignedToTypes = [],
    } = lookups

    const typeId = Number(rule.fixedAssignedToTypeId)
    const assigneeId = rule.fixedAssignedToId
    if (assigneeId == null || assigneeId === '') return rule.assigneeName || ''

    const typeRow = assignedToTypes.find(t => Number(t.id) === typeId)
    const typeName = String(
        typeRow?.name || rule.fixedAssignedToTypeName || '',
    ).toLowerCase()

    if (typeId === 1 || typeName.includes('user')) {
        const member = findTeamMemberByUserId(teamMembers, assigneeId)
        if (member) {
            const label =
                member.FullName?.trim() ||
                member.Name?.trim() ||
                member.Email?.trim() ||
                (typeof member.UserId === 'string' ? member.UserId.trim() : '')
            if (label) return label
        }
        return rule.assigneeName || `User #${assigneeId}`
    }

    if (typeId === 2 || typeName.includes('team')) {
        const team = teams.find(t => Number(t.id) === Number(assigneeId))
        return team?.name || rule.assigneeName || `Team #${assigneeId}`
    }

    if (typeId === 3 || typeName.includes('vendorcompany')) {
        const vendor = vendorCompanies.find(
            v => Number(v.id) === Number(assigneeId),
        )
        return vendor?.name || rule.assigneeName || `Vendor #${assigneeId}`
    }

    if (typeId === 4 || typeName.includes('vendorcontact')) {
        return rule.assigneeName || `Contact #${assigneeId}`
    }

    return rule.assigneeName || ''
}

export const getRuleTriggerValues = rule => {
    if (rule?.triggerValues?.length) {
        return rule.triggerValues.map(v => String(v)).filter(Boolean)
    }
    if (rule?.triggerValue) return [String(rule.triggerValue)]
    return []
}

export const formatTriggerValuesLabel = (rule, separator = ', ') => {
    const values = getRuleTriggerValues(rule)
    return values.length ? values.join(separator) : '—'
}

export const getRuleAssignees = rule => {
    if (rule?.assignees?.length) return rule.assignees
    if (rule?.fixedAssignedToTypeId != null && rule?.fixedAssignedToTypeId !== '') {
        return [
            {
                typeId: rule.fixedAssignedToTypeId,
                assignedToId: rule.fixedAssignedToId,
                vendorCompanyId: null,
                assigneeName: rule.assigneeName || '',
            },
        ]
    }
    return []
}

export const formatRuleAssigneesLabel = (rule, lookups = {}) => {
    const assignees = getRuleAssignees(rule)
    if (!assignees.length) return rule.assigneeName || '—'
    const labels = assignees
        .map(a => {
            if (a.assigneeName) return a.assigneeName
            return resolveRuleAssigneeName(
                {
                    ...rule,
                    fixedAssignedToTypeId: a.typeId,
                    fixedAssignedToId: a.assignedToId,
                },
                lookups,
            )
        })
        .filter(Boolean)
    return labels.length ? labels.join('; ') : rule.assigneeName || '—'
}

export const triggersOverlap = (ruleA, ruleB) => {
    const setA = new Set(
        getRuleTriggerValues(ruleA).map(v => v.toLowerCase()),
    )
    return getRuleTriggerValues(ruleB).some(v => setA.has(v.toLowerCase()))
}

export const enrichRulesWithLookups = (rules, lookups = {}) =>
    rules.map(rule => {
        const priority = (lookups.priorities || []).find(
            p => Number(p.id) === Number(rule.priorityId),
        )
        const category = (lookups.categories || []).find(
            c => Number(c.id) === Number(rule.categoryId),
        )
        const typeRow = (lookups.assignedToTypes || []).find(
            t => Number(t.id) === Number(rule.fixedAssignedToTypeId),
        )
        const assignees = getRuleAssignees(rule).map(a => ({
            ...a,
            assigneeName:
                a.assigneeName ||
                resolveRuleAssigneeName(
                    {
                        ...rule,
                        fixedAssignedToTypeId: a.typeId,
                        fixedAssignedToId: a.assignedToId,
                    },
                    lookups,
                ),
        }))
        const assigneeName = formatRuleAssigneesLabel(
            { ...rule, assignees },
            lookups,
        )
        return {
            ...rule,
            assignees,
            priorityName: rule.priorityName || priority?.name || '',
            categoryName: rule.categoryName || category?.name || '',
            fixedAssignedToTypeName:
                rule.fixedAssignedToTypeName || typeRow?.name || '',
            assigneeName,
        }
    })

export const normalizePriorityList = response =>
    (Array.isArray(response) ? response : []).map(item => ({
        id: item.PriorityId ?? item.Id ?? item.id,
        name:
            item.PriorityName ??
            item.Name ??
            item.name ??
            '',
    }))

export const normalizeCategoryList = response =>
    (Array.isArray(response) ? response : []).map(item => ({
        id: item.CategoryId ?? item.Id ?? item.id,
        name:
            item.CategoryName ??
            item.Name ??
            item.name ??
            '',
    }))

export const normalizeAssignedToTypeList = response =>
    (Array.isArray(response) ? response : []).map(item => ({
        id: Number(item.AssignedToTypeId ?? item.Id ?? item.id),
        name:
            item.TypeName ??
            item.Name ??
            item.name ??
            '',
    }))

export const normalizeTeamGroupList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list.map(item => ({
        id: item.GroupId ?? item.Id ?? item.id,
        name:
            item.GroupName ??
            item.DisplayName ??
            item.Name ??
            item.name ??
            '',
    }))
}

export const normalizeActionPlanTeamList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list
        .map(item => ({
            id: item.TeamId ?? item.Id ?? item.id,
            name:
                item.TeamName ??
                item.DisplayName ??
                item.Name ??
                item.name ??
                '',
        }))
        .filter(item => item.id != null && item.id !== '')
}

export const normalizeVendorCompanyList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list
        .map(item => ({
            id:
                item.VendorCompanyId ??
                item.CompanyId ??
                item.Id ??
                item.id,
            name:
                item.CompanyName ??
                item.DisplayName ??
                item.VendorName ??
                item.Name ??
                item.name ??
                '',
        }))
        .filter(item => item.id != null && item.id !== '')
}

export const normalizeVendorContactList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list
        .map(item => ({
            id:
                item.VendorContactId ??
                item.ContactId ??
                item.Id ??
                item.id,
            name:
                item.ContactName ??
                item.DisplayName ??
                item.FullName ??
                item.Name ??
                item.name ??
                '',
            email: item.Email ?? item.email ?? '',
            vendorCompanyId:
                item.VendorCompanyId ?? item.CompanyId ?? item.vendorCompanyId,
            phone: item.Phone ?? item.phone ?? '',
            whatsAppNumber: item.WhatsAppNumber ?? item.whatsAppNumber ?? '',
            isPrimary: Boolean(item.IsPrimary ?? item.isPrimary),
        }))
        .filter(item => item.id != null && item.id !== '')
}

export const ADD_VENDOR_COMPANY_KEY = '__add_vendor_company__'
export const ADD_VENDOR_CONTACT_KEY = '__add_vendor_contact__'

export const withAddVendorCompanyOption = options => [
    ...(options || []),
    {
        key: ADD_VENDOR_COMPANY_KEY,
        value: ADD_VENDOR_COMPANY_KEY,
        text: '+ Add new vendor company…',
        className: 'vendor-add-new-option',
    },
]

export const withAddVendorContactOption = options => [
    ...(options || []),
    {
        key: ADD_VENDOR_CONTACT_KEY,
        value: ADD_VENDOR_CONTACT_KEY,
        text: '+ Add new vendor contact…',
        className: 'vendor-add-new-option',
    },
]

export const normalizeVendorCompanyFromResponse = response => ({
    id: response?.VendorCompanyId ?? response?.id,
    name: response?.VendorName ?? response?.name ?? '',
})

export const normalizeVendorContactFromResponse = response => ({
    id: response?.VendorContactId ?? response?.id,
    name: response?.FullName ?? response?.name ?? '',
    email: response?.Email ?? response?.email ?? '',
    phone: response?.Phone ?? response?.phone ?? '',
    whatsAppNumber: response?.WhatsAppNumber ?? response?.whatsAppNumber ?? '',
    isPrimary: Boolean(response?.IsPrimary ?? response?.isPrimary),
    vendorCompanyId:
        response?.VendorCompanyId ?? response?.vendorCompanyId ?? null,
})

export const normalizeRoleList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list.map(item => ({
        id: item.RoleId ?? item.Id ?? item.id,
        name: item.RoleName ?? item.Name ?? item.name ?? '',
    }))
}

export const assigneePanelKeyFromType = type => {
    const n = String(type?.name || '').toLowerCase()
    if (n.includes('vendorcontact')) return 'vendor-contact'
    if (n.includes('vendorcompany')) return 'vendor-company'
    if (n.includes('internalteam') || n.includes('team')) return 'team'
    if (n.includes('role')) return 'role'
    if (n.includes('internaluser') || n.includes('user')) return 'user'
    return null
}

/** @deprecated use assigneePanelKeyFromType */
export const assigneeKeyFromTypeName = name =>
    assigneePanelKeyFromType({ name })

/** @deprecated use assigneePanelKeyFromType with API type row */
export const assigneeKeyFromTypeId = typeId => {
    const id = Number(typeId)
    if (id === ASSIGNEE_TYPE_KEYS.user) return 'user'
    if (id === ASSIGNEE_TYPE_KEYS.team) return 'team'
    if (id === ASSIGNEE_TYPE_KEYS.vendorCompany) return 'vendor-company'
    if (id === ASSIGNEE_TYPE_KEYS.vendorContact) return 'vendor-contact'
    return null
}

export const getAssigneeOptionUi = type => {
    const panelKey = assigneePanelKeyFromType(type)
    const ui = panelKey ? ASSIGNEE_OPTION_UI[panelKey] : null
    return {
        panelKey,
        title: ui?.title || type?.name || 'Assignee',
        desc: ui?.desc || '',
    }
}

export const FALLBACK_ASSIGNEE_TYPES = [
    { id: 1, name: 'InternalUser' },
    { id: 2, name: 'InternalTeam' },
    { id: 3, name: 'VendorCompany' },
    { id: 4, name: 'VendorContact' },
]

export const DEFAULT_ASSIGNEE_TYPES = FALLBACK_ASSIGNEE_TYPES

export const resolveAssigneeTypes = types => {
    const normalized = normalizeAssignedToTypeList(types)
    if (!normalized.length) return FALLBACK_ASSIGNEE_TYPES
    return normalized.sort((a, b) => Number(a.id) - Number(b.id))
}

/** @deprecated use resolveAssigneeTypes */
export const dedupeAssignedToTypes = resolveAssigneeTypes

export const findTeamMemberByUserId = (members, userId) =>
    (members || []).find(m => {
        const target = String(userId)
        const candidateIds = [
            m.UserId,
            m.unique_id,
            m.UniqueId,
            m.Id,
            m.id,
        ]
            .filter(v => v != null && v !== '')
            .map(String)
        return candidateIds.includes(target)
    })

export const formatUserPickerLabel = user => {
    const name = user?.name?.trim()
    if (name) {
        return user.role?.trim() ? `${name} — ${user.role}` : name
    }
    const email = user?.email?.trim()
    if (email) return email
    if (user?.id != null && user.id !== '') {
        return `User #${user.id}`
    }
    return 'Unknown user'
}

export const normalizeTemplateUsersList = response => {
    const list = Array.isArray(response)
        ? response
        : response?.Items || response?.Data || []
    return list
        .map(u => ({
            id: u.UserId ?? u.unique_id ?? u.Id ?? u.id,
            name:
                u.FullName ??
                u.Name ??
                `${u.FirstName || ''} ${u.LastName || ''}`.trim(),
            email: u.Email ?? u.email ?? '',
            role: u.RoleName ?? u.JobTitle ?? '',
        }))
        .filter(u => u.id != null && u.id !== '')
}

export const mergeTemplateUsersForPicker = (
    byTemplateRes,
    byListRes,
    allTeamMembers,
) => {
    const merged = []
    const seen = new Set()
    const sources = [byTemplateRes, byListRes]
    sources.forEach(source => {
        normalizeTemplateUsersList(source).forEach(u => {
            const key = String(u.id)
            if (!seen.has(key)) {
                seen.add(key)
                merged.push(u)
            }
        })
    })
    if (merged.length) {
        return enrichTemplateUsersWithTeamMembers(merged, allTeamMembers)
    }
    if (allTeamMembers?.length) {
        return normalizeTemplateUsersList(allTeamMembers)
    }
    return []
}

export const enrichTemplateUsersWithTeamMembers = (
    assignedList,
    allMembers,
) => {
    const assigned = normalizeTemplateUsersList(assignedList)
    if (!assigned.length) return assigned

    return assigned.map(au => {
        if (au.name?.trim()) return au
        const member = findTeamMemberByUserId(allMembers, au.id)
        if (!member) {
            return {
                ...au,
                name: au.email?.trim() ? au.email : '',
            }
        }
        const name =
            member.FullName ??
            member.Name ??
            `${member.FirstName || ''} ${member.LastName || ''}`.trim()
        return {
            id: au.id,
            name: name || member.Email || '',
            email: member.Email ?? au.email,
            role: member.RoleName ?? member.JobTitle ?? au.role,
        }
    })
}

export const toDropdownOptions = (
    items,
    { getValue = i => i.id, getText = i => i.name, getDescription } = {},
) =>
    (items || []).map(item => {
        const value = getValue(item)
        const rawText = getText(item)
        return {
            key: String(value),
            value,
            text: rawText?.trim() ? rawText : String(value),
            ...(getDescription
                ? { description: getDescription(item) || '' }
                : {}),
        }
    })

export const getPriorityLevel = name => {
    const n = String(name || '').toLowerCase()
    if (n.includes('critical')) return 'critical'
    if (n.includes('high')) return 'high'
    if (n.includes('medium') || n.includes('med')) return 'medium'
    return 'low'
}

export const formatMinutesLabel = minutes => {
    const m = Number(minutes)
    if (!Number.isFinite(m) || m <= 0) return '—'
    if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`
    if (m < 1440) {
        const h = Math.round(m / 60)
        return `${h} hour${h === 1 ? '' : 's'}`
    }
    const days = Math.round(m / 1440)
    return `${days} day${days === 1 ? '' : 's'}`
}

export const formatAssigneeIcon = typeName => {
    const t = String(typeName || '').toLowerCase()
    if (t.includes('vendorcontact')) return '📇'
    if (t.includes('vendorcompany') || t.includes('vendor')) return '🏢'
    if (t.includes('team')) return '👥'
    if (t.includes('role')) return '🎭'
    return '👤'
}

export const computeRuleStats = (rules, ticketCount = null) => {
    const total = rules.length
    const enabled = rules.filter(r => r.isEnabled).length
    const disabled = total - enabled
    const tickets =
        ticketCount ??
        rules.reduce(
            (sum, r) => sum + (Number(r.ticketsLast30Days) || 0),
            0,
        )
    return { total, enabled, disabled, tickets }
}

/**
 * SLA defaults in minutes (API: DefaultAckSlaMinutes / DefaultResolveSlaMinutes).
 * Critical is capped at 1 hour (client: "in critical we allow only 1 hours").
 */
export const CRITICAL_MAX_SLA_MINUTES = 60

export const DEFAULT_PRIORITY_SLA = {
    low: { ack: 1440, resolve: 10080 }, // 24h / 7d
    medium: { ack: 240, resolve: 2880 }, // 4h / 48h
    high: { ack: 120, resolve: 1440 }, // 2h / 24h
    critical: { ack: 15, resolve: 60 }, // 15m / 1h (max 60)
}

/** @deprecated use DEFAULT_PRIORITY_SLA (minutes) */
export const DEFAULT_PRIORITY_SLA_HOURS = {
    low: { ack: 24, resolve: 168 },
    medium: { ack: 4, resolve: 48 },
    high: { ack: 2, resolve: 24 },
    critical: { ack: 1, resolve: 1 },
}

export const getSlaMaxMinutesForPriority = priorityLevel =>
    String(priorityLevel || '').toLowerCase() === 'critical'
        ? CRITICAL_MAX_SLA_MINUTES
        : null

export const clampSlaMinutes = (minutes, priorityLevel) => {
    const n = Number(minutes)
    if (!Number.isFinite(n) || n <= 0) return minutes
    const max = getSlaMaxMinutesForPriority(priorityLevel)
    if (max == null) return Math.max(1, Math.round(n))
    return Math.min(max, Math.max(1, Math.round(n)))
}

export const FALLBACK_PRIORITIES = [
    { id: 1, name: 'Low' },
    { id: 2, name: 'Medium' },
    { id: 3, name: 'High' },
    { id: 4, name: 'Critical' },
]

export const buildRulePayload = (form, templateId, isEdit = false) => {
    // Prefer minutes (UI now accepts minutes). Fall back to legacy hours fields.
    let ackMinutes = Number(form.ackSlaMinutes)
    if (!Number.isFinite(ackMinutes) || ackMinutes <= 0) {
        const ackHours = Number(form.ackSlaHours)
        ackMinutes =
            Number.isFinite(ackHours) && ackHours > 0 ? ackHours * 60 : null
    }
    let resolveMinutes = Number(form.resolveSlaMinutes)
    if (!Number.isFinite(resolveMinutes) || resolveMinutes <= 0) {
        const resolveHours = Number(form.resolveSlaHours)
        resolveMinutes =
            Number.isFinite(resolveHours) && resolveHours > 0
                ? resolveHours * 60
                : null
    }
    if (Number.isFinite(ackMinutes) && ackMinutes > 0) {
        ackMinutes = Math.round(ackMinutes)
    } else {
        ackMinutes = null
    }
    if (Number.isFinite(resolveMinutes) && resolveMinutes > 0) {
        resolveMinutes = Math.round(resolveMinutes)
    } else {
        resolveMinutes = null
    }

    let triggerValues = []
    if (Array.isArray(form.triggerValues) && form.triggerValues.length) {
        triggerValues = form.triggerValues.map(v => String(v)).filter(Boolean)
    } else if (form.triggerValue) {
        triggerValues = [String(form.triggerValue)]
    }

    let assignees = [
        {
            typeId: form.fixedAssignedToTypeId,
            assignedToId: form.fixedAssignedToId,
            vendorCompanyId: form.vendorCompanyId,
        },
    ]
    if (Array.isArray(form.assignees) && form.assignees.length) {
        assignees = form.assignees
    }
    const primary = assignees[0] || {}

    const payload = {
        TemplateId: Number(templateId) || templateId,
        AuditQuestionId: Number(form.auditQuestionId),
        TriggerOperatorId: Number(form.triggerOperatorId) || 1,
        TriggerValue: triggerValues[0] || '',
        TriggerValues: triggerValues,
        RuleTriggerAnswers: triggerValues.map(v => ({ TriggerValue: v })),
        TicketType: form.ticketType || 'General',
        PriorityId: Number(form.priorityId),
        CategoryId: Number(form.categoryId),
        DefaultAckSlaMinutes: ackMinutes,
        DefaultResolveSlaMinutes: resolveMinutes,
        AssignmentStrategy: ASSIGNMENT_STRATEGY_FIXED,
        FixedAssignedToTypeId: Number(primary.typeId),
        FixedAssignedToId: Number(primary.assignedToId),
        Assignees: assignees.map(a => ({
            FixedAssignedToTypeId: Number(a.typeId),
            FixedAssignedToId: Number(a.assignedToId),
            ...(a.vendorCompanyId
                ? { VendorCompanyId: Number(a.vendorCompanyId) }
                : {}),
        })),
        EscalationPolicyId: form.escalationPolicyId
            ? Number(form.escalationPolicyId)
            : null,
    }
    const customerIds = parseIdList(form.customerIds)
    const limitToCustomers = form.customerMode === 'selected'
    if (limitToCustomers) {
        payload.CustomerIds = customerIds
    } else if (isEdit) {
        // Empty array returns the rule to every customer. Omitting the field
        // on update would leave the previous customers in place.
        payload.CustomerIds = []
    }
    if (isEdit && form.ruleId != null && form.ruleId !== '') {
        payload.RuleId = Number(form.ruleId)
    }
    if (isEdit) {
        payload.IsEnabled = form.isEnabled !== false
    }
    return payload
}

export const ruleMatchesSearch = (rule, search) => {
    if (!search?.trim()) return true
    const q = search.trim().toLowerCase()
    const triggerHaystack = getRuleTriggerValues(rule)
        .join(' ')
        .toLowerCase()
    const assigneeHaystack = getRuleAssignees(rule)
        .map(a => a.assigneeName)
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    const customerHaystack = [
        formatRuleCustomersLabel(rule),
        ...getRuleCustomerNames(rule),
    ]
        .join(' ')
        .toLowerCase()
    return (
        rule.questionText?.toLowerCase().includes(q) ||
        rule.sectionName?.toLowerCase().includes(q) ||
        rule.assigneeName?.toLowerCase().includes(q) ||
        assigneeHaystack.includes(q) ||
        triggerHaystack.includes(q) ||
        customerHaystack.includes(q) ||
        rule.categoryName?.toLowerCase().includes(q) ||
        rule.ticketType?.toLowerCase().includes(q)
    )
}

export const answerOffersTrigger = (question, triggerValue) => {
    if (!triggerValue) return false
    const options = getQuestionAnswerOptions(question)
    const tv = String(triggerValue).toLowerCase()
    return options.some(o => String(o.text).toLowerCase() === tv)
}

export const answerOffersAllTriggers = (question, triggerValues) => {
    let values = []
    if (Array.isArray(triggerValues)) {
        values = triggerValues
    } else if (triggerValues) {
        values = [triggerValues]
    }
    if (!values.length) return false
    return values.every(tv => answerOffersTrigger(question, tv))
}

export const getApiErrorMessage = err => {
    const data = err?.error ?? err?.response?.data
    if (typeof data === 'string') return data
    return (
        data?.Message ??
        data?.message ??
        err?.message ??
        'Something went wrong'
    )
}

export const parseRuleCountResponse = result => ({
    total: Number(result?.Total ?? result?.total ?? 0),
    enabled: Number(result?.Enabled ?? result?.enabled ?? 0),
})

/** Numeric unique_id from GET /user/Template (Action Plan APIs use this). */
export const resolveTemplateNumericId = template => {
    const id = template?.Id ?? template?.unique_id
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
}
